import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { User, Mailbox } from '../types';
import api from '../services/api';
import websocketService from '../services/websocket';
import Tooltip from './Tooltip';
import { formatDateMsk } from '../utils/dateTime';
import createRipple from '../utils/ripple';
import { mailboxAddress } from '../utils/mailboxAddress';
import './MailboxList.css';

interface MailboxListProps {
  onMailboxSelect: (mailboxId: string, mailboxData?: Mailbox) => void;
  user: User;
  selectedMailbox?: string | null;
  onShowNotification?: (message: string, type: 'error' | 'success') => void;
  onMailboxDeleted?: (mailboxId: string) => void;
}

const MailboxList: React.FC<MailboxListProps> = ({ onMailboxSelect, user, selectedMailbox, onShowNotification, onMailboxDeleted }) => {
  // Keep list realtime, but batch WS events so high-throughput bursts do not
  // saturate the UI thread in anti-detect browsers.
  const enableRealtimeMailboxList = true;
  const MAILBOX_ITEM_ESTIMATED_HEIGHT = 98;
  const MAILBOX_VIRTUAL_OVERSCAN_PX = 320;
  // If a user becomes a child after login, `isChild` might be stale in localStorage.
  // Treat parentUserId as the source of truth when present.
  const isChild = Boolean((user as any)?.isChild || (user as any)?.parentUserId != null);
  const [mailboxesByScope, setMailboxesByScope] = useState<Record<'mine' | 'all', Mailbox[]>>({
    mine: [],
    all: []
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  // Входная каскадная анимация: активна ~1.6с после первой загрузки списка,
  // чтобы не проигрываться заново при скролле (виртуализация) и обновлениях.
  const [entranceActive, setEntranceActive] = useState(true);
  const entranceArmedRef = useRef(false);
  useEffect(() => {
    if (isLoading || entranceArmedRef.current) return;
    entranceArmedRef.current = true;
    const timer = window.setTimeout(() => setEntranceActive(false), 1600);
    return () => window.clearTimeout(timer);
  }, [isLoading]);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [hoveredMailboxId, setHoveredMailboxId] = useState<string | null>(null);
  const [brokenChildAvatarUrls, setBrokenChildAvatarUrls] = useState<Record<string, true>>({});
  const [scope, setScope] = useState<'mine' | 'all'>('mine');
  const scopeRef = useRef<'mine' | 'all'>(scope);
  const searchTermRef = useRef(searchTerm);
  const isChildRef = useRef(isChild);
  const [pageByScope, setPageByScope] = useState<Record<'mine' | 'all', number>>({ mine: 1, all: 1 });
  const [hasMoreByScope, setHasMoreByScope] = useState<Record<'mine' | 'all', boolean>>({ mine: true, all: true });
  const getInitialTotals = (): Record<'mine' | 'all', number | null> => {
    try {
      const raw = localStorage.getItem('ms_totals_v1');
      if (!raw) return { mine: null, all: null };
      const parsed = JSON.parse(raw);
      const mine = typeof parsed?.mine === 'number' ? parsed.mine : null;
      const all = typeof parsed?.all === 'number' ? parsed.all : null;
      return { mine, all };
    } catch {
      return { mine: null, all: null };
    }
  };
  const [totalByScope, setTotalByScope] = useState<Record<'mine' | 'all', number | null>>(getInitialTotals);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const limit = 30;
  const notifyRef = useRef(onShowNotification);
  const searchDebounceRef = useRef<number | null>(null);
  const skipFirstSearchEffectRef = useRef(true);
  const wsDedupRef = useRef<Map<string, number>>(new Map());
  const wsPendingUpdatesRef = useRef<Map<string, { delta: number; lastEmailAt: string | null }>>(new Map());
  const wsFlushTimerRef = useRef<number | null>(null);
  const wsResyncTimerRef = useRef<number | null>(null);
  const transferSearchDebounceRef = useRef<number | null>(null);
  const lastTransferSearchKeyRef = useRef('');
  const wsWindowStartRef = useRef<number>(Date.now());
  const wsEventsInWindowRef = useRef<number>(0);
  const wsRealtimePausedUntilRef = useRef<number>(0);
  const mailboxBaseMetaRef = useRef<Record<string, { emailCount: number; lastEmailAt: string | null }>>({});
  const [mailboxLiveMetaById, setMailboxLiveMetaById] = useState<Record<string, { emailCount: number; lastEmailAt: string | null }>>({});
  const mailboxItemsRef = useRef<HTMLDivElement | null>(null);
  const mailboxScrollRafRef = useRef<number | null>(null);
  const mailboxPendingScrollTopRef = useRef(0);
  const [mailboxListScrollTop, setMailboxListScrollTop] = useState(0);
  const [mailboxListViewportHeight, setMailboxListViewportHeight] = useState(0);
  const [highlightedMailboxIds, setHighlightedMailboxIds] = useState<Set<string>>(new Set());
  const highlightTimersRef = useRef<number[]>([]);
  const [deleteCandidate, setDeleteCandidate] = useState<Mailbox | null>(null);
  const [deletingMailboxId, setDeletingMailboxId] = useState<string | null>(null);
  const [transferCandidate, setTransferCandidate] = useState<Mailbox | null>(null);
  const [transferOptions, setTransferOptions] = useState<any | null>(null);
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferSaving, setTransferSaving] = useState(false);
  const [selectedTransferChildIds, setSelectedTransferChildIds] = useState<Set<number>>(new Set());
  const [kingReceivesMetaNotifications, setKingReceivesMetaNotifications] = useState(false);
  const [transferSearchTerm, setTransferSearchTerm] = useState('');

  const effectiveScope: 'mine' | 'all' = isChild ? 'mine' : scope;
  const mailboxes = mailboxesByScope[effectiveScope];
  const page = pageByScope[effectiveScope];
  const hasMore = hasMoreByScope[effectiveScope];
  const optimisticCreatedMailboxIdsRef = useRef<Set<string>>(new Set());
  const pendingCreateRequestIdsRef = useRef<Set<string>>(new Set());
  const countedCreateRequestIdsRef = useRef<Set<string>>(new Set());

  const makeClientRequestId = () => {
    try {
      // Modern browsers
      const anyCrypto: any = (globalThis as any).crypto;
      if (anyCrypto?.randomUUID) return String(anyCrypto.randomUUID());
    } catch {
      // ignore
    }
    return `req_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  };

  const flashMailboxIds = useCallback((ids: string[]) => {
    const uniqueIds = Array.from(new Set(ids.filter(Boolean).map(String)));
    if (uniqueIds.length === 0) return;

    setHighlightedMailboxIds(prev => {
      const next = new Set(prev);
      uniqueIds.forEach(id => next.add(id));
      return next;
    });

    const timer = window.setTimeout(() => {
      setHighlightedMailboxIds(prev => {
        let changed = false;
        const next = new Set(prev);
        uniqueIds.forEach(id => {
          if (next.delete(id)) changed = true;
        });
        return changed ? next : prev;
      });
    }, 2600);
    highlightTimersRef.current.push(timer);
  }, []);

  useEffect(() => {
    return () => {
      highlightTimersRef.current.forEach(timer => window.clearTimeout(timer));
      highlightTimersRef.current = [];
    };
  }, []);

  const normalizeMailbox = (mb: any): Mailbox => {
    const id = mb?.id != null ? String(mb.id) : '';
    return {
      ...mb,
      id
    } as Mailbox;
  };

  const sortMailboxes = useCallback((list: Mailbox[]) => {
    // Стабильная сортировка: "активность" (письмо или создание) -> дата создания
    const toTime = (v?: string | null) => (v ? Date.parse(v) : NaN);
    return [...list].sort((a, b) => {
      const aT = Number.isFinite(toTime(a.lastEmailAt)) ? toTime(a.lastEmailAt) : toTime(a.createdAt);
      const bT = Number.isFinite(toTime(b.lastEmailAt)) ? toTime(b.lastEmailAt) : toTime(b.createdAt);
      const aHas = Number.isFinite(aT);
      const bHas = Number.isFinite(bT);
      if (aHas && bHas) return bT - aT;
      if (aHas !== bHas) return aHas ? -1 : 1;
      return Date.parse(b.createdAt) - Date.parse(a.createdAt);
    });
  }, []);

  const pluralizeRu = (count: number, one: string, few: string, many: string) => {
    const n = Math.abs(count);
    const n10 = n % 10;
    const n100 = n % 100;
    if (n10 === 1 && n100 !== 11) return one;
    if (n10 >= 2 && n10 <= 4 && !(n100 >= 12 && n100 <= 14)) return few;
    return many;
  };

  useEffect(() => {
    notifyRef.current = onShowNotification;
  }, [onShowNotification]);

  useEffect(() => {
    scopeRef.current = scope;
  }, [scope]);

  useEffect(() => {
    searchTermRef.current = searchTerm;
  }, [searchTerm]);

  useEffect(() => {
    isChildRef.current = isChild;
  }, [isChild]);

  useEffect(() => {
    const el = mailboxItemsRef.current;
    if (!el) return;
    const updateViewport = () => setMailboxListViewportHeight(el.clientHeight || 0);
    updateViewport();
    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, [mailboxes.length]);

  useEffect(() => {
    return () => {
      if (mailboxScrollRafRef.current != null) {
        window.cancelAnimationFrame(mailboxScrollRafRef.current);
        mailboxScrollRafRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    // Child accounts never have access to "all".
    if (isChild && scope !== 'mine') {
      setScope('mine');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Чтобы на кнопках сразу были оба числа (и mine, и all) — подгружаем totals фоном.
  useEffect(() => {
    let cancelled = false;
    const loadTotals = async () => {
      try {
        const [mineResp, allResp] = await Promise.all([
          api.getMailboxes({ scope: 'mine', page: 1, limit: 1, q: '' }),
          isChild ? Promise.resolve(null as any) : api.getMailboxes({ scope: 'all', page: 1, limit: 1, q: '' })
        ]);

        if (cancelled) return;

        const mineTotal = mineResp.success && (mineResp.data as any)?.pagination
          ? (mineResp.data as any).pagination.total
          : null;
        const allTotal = allResp && allResp.success && (allResp.data as any)?.pagination
          ? (allResp.data as any).pagination.total
          : null;

        setTotalByScope(prev => ({
          mine: typeof mineTotal === 'number' ? mineTotal : prev.mine,
          all: typeof allTotal === 'number' ? allTotal : prev.all
        }));
      } catch {
        // totals — не критично, молча игнорируем
      }
    };

    loadTotals();
    return () => {
      cancelled = true;
    };
  }, [user.id, (user as any)?.isChild]);

  useEffect(() => {
    // Кешируем totals, чтобы при следующем заходе не было "0 → число".
    try {
      localStorage.setItem('ms_totals_v1', JSON.stringify(totalByScope));
    } catch {
      // ignore
    }
  }, [totalByScope]);

  useEffect(() => {
    const next: Record<string, { emailCount: number; lastEmailAt: string | null }> = {};
    const all = [...mailboxesByScope.mine, ...mailboxesByScope.all];
    for (const mb of all) {
      const id = String(mb.id);
      next[id] = {
        emailCount: Number(mb.emailCount || 0),
        lastEmailAt: mb.lastEmailAt || null
      };
    }
    mailboxBaseMetaRef.current = next;
    setMailboxLiveMetaById(prev => {
      const pruned: typeof prev = {};
      let changed = false;
      Object.keys(prev).forEach((id) => {
        const base = next[id];
        const live = prev[id];
        if (!base) {
          changed = true;
          return;
        }
        const liveTime = live.lastEmailAt ? Date.parse(live.lastEmailAt) : 0;
        const baseTime = base.lastEmailAt ? Date.parse(base.lastEmailAt) : 0;
        if (Number(live.emailCount || 0) <= Number(base.emailCount || 0) && liveTime <= baseTime) {
          changed = true;
          return;
        }
        pruned[id] = live;
      });
      return changed ? pruned : prev;
    });
  }, [mailboxesByScope]);

  const [newMailboxName, setNewMailboxName] = useState('');
  const [newMailboxAccount, setNewMailboxAccount] = useState('');
  const [newMailboxPassword, setNewMailboxPassword] = useState('');
  const [newMailboxNotePrivate, setNewMailboxNotePrivate] = useState(true);
  const [formExpanded, setFormExpanded] = useState(false);

  const loadMailboxes = useCallback(async (opts: {
    nextScope: 'mine' | 'all';
    nextPage: number;
    q: string;
    append?: boolean;
    silent?: boolean;
    allowFlash?: boolean;
  }) => {
    const silent = Boolean(opts.silent);
    const append = Boolean(opts.append);
    const allowFlash = opts.allowFlash !== false;
    const targetScope: 'mine' | 'all' = isChild ? 'mine' : opts.nextScope;
    try {
      if (!silent) {
        if (append) setIsLoadingMore(true);
        else setIsLoading(true);
      }

      const response = await api.getMailboxes({
        scope: targetScope,
        q: opts.q,
        page: opts.nextPage,
        limit
      });
      if (response.success && response.data) {
        const incoming = sortMailboxes((response.data.mailboxes || []).map(normalizeMailbox));

        setMailboxesByScope(prev => {
          const prevList = prev[targetScope];
          const flashIds: string[] = [];
          if (allowFlash && silent && opts.nextPage === 1 && !append) {
            const prevById = new Map(prevList.map(mb => [String(mb.id), mb]));
            incoming.forEach(mb => {
              const prevMailbox = prevById.get(String(mb.id));
              if (!prevMailbox) {
                if (prevList.length > 0) flashIds.push(String(mb.id));
                return;
              }
              const prevCount = Number(prevMailbox.emailCount || 0);
              const nextCount = Number(mb.emailCount || 0);
              const prevLast = String(prevMailbox.lastEmailAt || '');
              const nextLast = String(mb.lastEmailAt || '');
              if (nextCount > prevCount || (nextLast && nextLast !== prevLast)) {
                flashIds.push(String(mb.id));
              }
            });
          }
          const merged = append
            ? (() => {
                const byId = new Map<string, Mailbox>();
                for (const mb of prevList) byId.set(mb.id, mb);
                for (const mb of incoming) byId.set(mb.id, mb);
                return Array.from(byId.values());
              })()
            : (silent && opts.nextPage === 1)
              ? (() => {
                  const freshIds = new Set(incoming.map(mb => mb.id));
                  const tail = prevList.filter(mb => !freshIds.has(mb.id));
                  return [...incoming, ...tail];
                })()
              : incoming;
          if (flashIds.length > 0) {
            window.setTimeout(() => flashMailboxIds(flashIds), 0);
          }
          return {
            ...prev,
            [targetScope]: sortMailboxes(merged)
          };
        });

        const pagination = (response.data as any).pagination;
        if (pagination && typeof pagination.totalPages === 'number') {
          const nextHasMore = opts.nextPage < pagination.totalPages;
          setHasMoreByScope(prev => ({ ...prev, [targetScope]: nextHasMore }));
          setPageByScope(prev => ({ ...prev, [targetScope]: opts.nextPage }));
          if (typeof pagination.total === 'number') {
            setTotalByScope(prev => ({ ...prev, [targetScope]: pagination.total }));
          }
        } else {
          setHasMoreByScope(prev => ({ ...prev, [targetScope]: false }));
          setPageByScope(prev => ({ ...prev, [targetScope]: opts.nextPage }));
        }

        setHasLoadedOnce(true);
      } else {
        notifyRef.current?.(response.message || 'Ошибка при загрузке ящиков', 'error');
      }
    } catch (error: any) {
      notifyRef.current?.(error.message || 'Ошибка при загрузке ящиков', 'error');
    } finally {
      if (!silent) {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    }
  }, [limit, user.id, isChild, flashMailboxIds]);

  const loadMailboxesRef = useRef(loadMailboxes);
  useEffect(() => {
    loadMailboxesRef.current = loadMailboxes;
  }, [loadMailboxes]);

  const syncSearchTotals = useCallback(async (q: string) => {
    try {
      const [mineResp, allResp] = await Promise.all([
        api.getMailboxes({ scope: 'mine', page: 1, limit: 1, q }),
        isChild ? Promise.resolve(null as any) : api.getMailboxes({ scope: 'all', page: 1, limit: 1, q })
      ]);

      const mineTotal = mineResp.success && (mineResp.data as any)?.pagination
        ? (mineResp.data as any).pagination.total
        : null;
      const allTotal = allResp && allResp.success && (allResp.data as any)?.pagination
        ? (allResp.data as any).pagination.total
        : null;

      setTotalByScope(prev => ({
        mine: typeof mineTotal === 'number' ? mineTotal : prev.mine,
        all: typeof allTotal === 'number' ? allTotal : prev.all
      }));
    } catch {
      // Счётчики поиска не критичны: активная вкладка всё равно обновится основным запросом.
    }
  }, [isChild]);

  useEffect(() => {
    // Переключение вкладок: показываем уже загруженное и обновляем в фоне
    const nextScope: 'mine' | 'all' = isChild ? 'mine' : scope;
    loadMailboxes({
      nextScope,
      nextPage: 1,
      q: searchTerm,
      append: false,
      silent: mailboxesByScope[nextScope].length > 0,
      allowFlash: false
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, user.id, isChild]);

  useEffect(() => {
    // Серверный поиск с дебаунсом
    if (skipFirstSearchEffectRef.current) {
      skipFirstSearchEffectRef.current = false;
      return;
    }

    if (searchDebounceRef.current) {
      window.clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = window.setTimeout(() => {
      const s: 'mine' | 'all' = isChild ? 'mine' : scopeRef.current;
      setPageByScope(prev => ({ ...prev, [s]: 1 }));
      setHasMoreByScope(prev => ({ ...prev, [s]: true }));
      void syncSearchTotals(searchTerm);
      loadMailboxes({ nextScope: s, nextPage: 1, q: searchTerm, append: false });
    }, 300);

    return () => {
      if (searchDebounceRef.current) {
        window.clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
    };
  }, [loadMailboxes, searchTerm, syncSearchTotals]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    let cancelled = false;

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;

      const activeScope: 'mine' | 'all' = isChildRef.current ? 'mine' : scopeRef.current;
      const q = searchTermRef.current;

      // On tab return, do a silent resync in case the browser throttled/slept WS.
      void loadMailboxesRef.current({
        nextScope: activeScope,
        nextPage: 1,
        q,
        append: false,
        silent: true,
        allowFlash: false
      });

      // Sync counters as well.
      void (async () => {
        try {
          const [mineResp, allResp] = await Promise.all([
            api.getMailboxes({ scope: 'mine', page: 1, limit: 1, q: '' }),
            isChildRef.current ? Promise.resolve(null as any) : api.getMailboxes({ scope: 'all', page: 1, limit: 1, q: '' })
          ]);
          if (cancelled) return;

          const mineTotal = mineResp.success && (mineResp.data as any)?.pagination
            ? (mineResp.data as any).pagination.total
            : null;
          const allTotal = allResp && allResp.success && (allResp.data as any)?.pagination
            ? (allResp.data as any).pagination.total
            : null;

          setTotalByScope(prev => ({
            mine: typeof mineTotal === 'number' ? mineTotal : prev.mine,
            all: typeof allTotal === 'number' ? allTotal : prev.all
          }));
        } catch {
          // ignore: silent sync
        }
      })();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  useEffect(() => {
    if (enableRealtimeMailboxList) return;
    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      const activeScope: 'mine' | 'all' = isChildRef.current ? 'mine' : scopeRef.current;
      const q = searchTermRef.current;
      void loadMailboxesRef.current({
        nextScope: activeScope,
        nextPage: 1,
        q,
        append: false,
        silent: true,
        allowFlash: true
      });
    }, 15000);
    return () => window.clearInterval(timer);
  }, [enableRealtimeMailboxList]);

  // WebSocket: обновления списка ящиков (reorder + счетчики)
  useEffect(() => {
    if (!enableRealtimeMailboxList) return;

    const scheduleWsResync = () => {
      if (wsResyncTimerRef.current != null) return;
      wsResyncTimerRef.current = window.setTimeout(() => {
        wsResyncTimerRef.current = null;
        if (document.visibilityState !== 'visible') return;
        const activeScope: 'mine' | 'all' = isChildRef.current ? 'mine' : scopeRef.current;
        const q = searchTermRef.current;
        void loadMailboxesRef.current({
          nextScope: activeScope,
          nextPage: 1,
          q,
          append: false,
          silent: true,
          allowFlash: true
        });
      }, 900);
    };

    const flushWsUpdates = () => {
      const updates = wsPendingUpdatesRef.current;
      wsPendingUpdatesRef.current = new Map();
      wsFlushTimerRef.current = null;
      if (updates.size === 0) return;
      const updateIds = Array.from(updates.keys());
      flashMailboxIds(updateIds);

      setMailboxesByScope(prev => {
        let changed = false;
        const applyUpdates = (list: Mailbox[]) => {
          let listChanged = false;
          const next = list.map((mailbox) => {
            const upd = updates.get(String(mailbox.id));
            if (!upd) return mailbox;
            listChanged = true;
            const nextEmailCount = Math.max(0, Number(mailbox.emailCount || 0) + Number(upd.delta || 0));
            return {
              ...mailbox,
              emailCount: nextEmailCount,
              lastEmailAt: upd.lastEmailAt || mailbox.lastEmailAt || null
            };
          });
          if (!listChanged) return list;
          changed = true;
          return sortMailboxes(next);
        };

        const mine = applyUpdates(prev.mine);
        const all = applyUpdates(prev.all);
        return changed ? { mine, all } : prev;
      });

      setMailboxLiveMetaById(prev => {
        const next = { ...prev };
        updates.forEach((upd, mailboxId) => {
          const base = mailboxBaseMetaRef.current[mailboxId];
          if (!base && !prev[mailboxId]) return;
          const current = prev[mailboxId] || base || { emailCount: 0, lastEmailAt: null };
          next[mailboxId] = {
            emailCount: Math.max(0, Number(current.emailCount || 0) + Number(upd.delta || 0)),
            lastEmailAt: upd.lastEmailAt || current.lastEmailAt || null
          };
        });
        return next;
      });

      // Reconcile with server shortly after a WS batch. This catches mailboxes
      // that were not loaded in the current page and need to jump to the top.
      scheduleWsResync();
    };

    const unsubscribe = websocketService.onMailboxUpdated((payload: any) => {
      const now = Date.now();
      if (now < wsRealtimePausedUntilRef.current) return;
      if (now - wsWindowStartRef.current > 10_000) {
        wsWindowStartRef.current = now;
        wsEventsInWindowRef.current = 0;
      }
      wsEventsInWindowRef.current += 1;
      if (wsEventsInWindowRef.current > 250) {
        // Safety valve: keep UI responsive under event storms.
        wsRealtimePausedUntilRef.current = now + 15_000;
        wsPendingUpdatesRef.current = new Map();
        if (wsFlushTimerRef.current != null) {
          window.clearTimeout(wsFlushTimerRef.current);
          wsFlushTimerRef.current = null;
        }
        return;
      }

      const mailboxId = payload?.mailboxId?.toString?.() || payload?.mailboxId;
      if (!mailboxId) return;
      const normalizedId = String(mailboxId);
      const deltaRaw = Number(payload?.emailCountDelta);
      const delta = Number.isFinite(deltaRaw) ? deltaRaw : 1;
      const receivedAt = String(payload?.lastEmailAt || payload?.receivedAt || '');
      const dedupKey = `${normalizedId}|${receivedAt}|${delta}`;
      const prevSeenAt = wsDedupRef.current.get(dedupKey);
      if (typeof prevSeenAt === 'number' && (now - prevSeenAt) < 3500) return;
      wsDedupRef.current.set(dedupKey, now);
      if (wsDedupRef.current.size > 400) {
        Array.from(wsDedupRef.current.entries()).forEach(([k, ts]) => {
          if ((now - ts) > 15000) wsDedupRef.current.delete(k);
        });
      }

      // Disable per-item pulse on frequent WS traffic; it is expensive on big lists.
      const existing = wsPendingUpdatesRef.current.get(normalizedId);
      wsPendingUpdatesRef.current.set(normalizedId, {
        delta: Number(existing?.delta || 0) + delta,
        lastEmailAt: (payload?.lastEmailAt || payload?.receivedAt || existing?.lastEmailAt || null)
      });
      if (wsFlushTimerRef.current == null) {
        wsFlushTimerRef.current = window.setTimeout(flushWsUpdates, 260);
      }
    });

    return () => {
      if (wsFlushTimerRef.current != null) {
        window.clearTimeout(wsFlushTimerRef.current);
        wsFlushTimerRef.current = null;
      }
      if (wsResyncTimerRef.current != null) {
        window.clearTimeout(wsResyncTimerRef.current);
        wsResyncTimerRef.current = null;
      }
      unsubscribe();
    };
  }, [enableRealtimeMailboxList, flashMailboxIds, sortMailboxes]);

  const virtualization = useMemo(() => {
    const count = mailboxes.length;
    const useVirtualization = count > 40;
    if (count === 0 || !useVirtualization) {
      return { start: 0, end: count, topSpacer: 0, bottomSpacer: 0 };
    }

    const viewport = mailboxListViewportHeight > 0 ? mailboxListViewportHeight : 800;
    const from = Math.max(0, mailboxListScrollTop - MAILBOX_VIRTUAL_OVERSCAN_PX);
    const to = mailboxListScrollTop + viewport + MAILBOX_VIRTUAL_OVERSCAN_PX;
    const start = Math.max(0, Math.floor(from / MAILBOX_ITEM_ESTIMATED_HEIGHT));
    const end = Math.min(count, Math.ceil(to / MAILBOX_ITEM_ESTIMATED_HEIGHT));
    const topSpacer = start * MAILBOX_ITEM_ESTIMATED_HEIGHT;
    const bottomSpacer = Math.max(0, (count - end) * MAILBOX_ITEM_ESTIMATED_HEIGHT);
    return { start, end, topSpacer, bottomSpacer };
  }, [mailboxes.length, mailboxListScrollTop, mailboxListViewportHeight]);

  // WebSocket: изменение общего количества ящиков (Мои/Все)
  useEffect(() => {
    if (!enableRealtimeMailboxList) return;

    const unsubCreated = websocketService.onMailboxCreated((payload: any) => {
      const ownerIdNum = payload?.userId != null ? Number(payload.userId) : null;
      const kingUserIdNum = payload?.kingUserId != null ? Number(payload.kingUserId) : null;
      const mailboxId = payload?.mailboxId != null ? String(payload.mailboxId) : null;
      const clientRequestId = payload?.clientRequestId != null ? String(payload.clientRequestId) : null;

      // Dedupe: WS event can arrive before HTTP response.
      if (clientRequestId) {
        if (countedCreateRequestIdsRef.current.has(clientRequestId)) return;
        countedCreateRequestIdsRef.current.add(clientRequestId);
        pendingCreateRequestIdsRef.current.delete(clientRequestId);
      } else if (mailboxId && optimisticCreatedMailboxIdsRef.current.has(mailboxId)) {
        // Legacy fallback (older servers without clientRequestId)
        optimisticCreatedMailboxIdsRef.current.delete(mailboxId);
        return;
      }
      const mineDelta = (ownerIdNum === user.id || kingUserIdNum === user.id) ? 1 : 0;
      setTotalByScope(prev => ({
        mine: (prev.mine ?? 0) + mineDelta,
        all: (prev.all ?? 0) + 1
      }));
    });

    const unsubDeleted = websocketService.onMailboxDeleted((payload: any) => {
      const ownerId = payload?.userId;
      const kingUserId = payload?.kingUserId;
      const mailboxId = payload?.mailboxId != null ? String(payload.mailboxId) : null;

      // Remove from loaded lists to avoid stale rows.
      if (mailboxId) {
        setMailboxesByScope(prev => ({
          mine: prev.mine.filter(mb => mb.id !== mailboxId),
          all: prev.all.filter(mb => mb.id !== mailboxId),
        }));
      }

      setTotalByScope(prev => ({
        mine: Math.max(0, (prev.mine ?? 0) - ((ownerId === user.id || kingUserId === user.id) ? 1 : 0)),
        all: Math.max(0, (prev.all ?? 0) - 1)
      }));
    });

    return () => {
      unsubCreated();
      unsubDeleted();
    };
  }, [enableRealtimeMailboxList, user.id]);

  const handleLoadMore = async () => {
    if (!hasMore || isLoadingMore) return;
    const nextPage = page + 1;
    setPageByScope(prev => ({ ...prev, [effectiveScope]: nextPage }));
    await loadMailboxes({ nextScope: effectiveScope, nextPage, q: searchTerm, append: true });
  };

  const handleMailboxItemsScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    mailboxPendingScrollTopRef.current = e.currentTarget.scrollTop;
    if (mailboxScrollRafRef.current == null) {
      mailboxScrollRafRef.current = window.requestAnimationFrame(() => {
        mailboxScrollRafRef.current = null;
        setMailboxListScrollTop(mailboxPendingScrollTopRef.current);
      });
    }
  }, []);

  const generateRandomName = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 12; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewMailboxName(result);
  };

  const createNameInputRef = useRef<HTMLInputElement | null>(null);
  const expandPendingRef = useRef(false);

  const toggleCreateForm = () => {
    if (formExpanded) {
      setFormExpanded(false);
      return;
    }
    // Раскрытие с небольшой задержкой: даём ripple на кнопке отыграть,
    // затем кнопка «делится» на две
    if (expandPendingRef.current) return;
    expandPendingRef.current = true;
    window.setTimeout(() => {
      expandPendingRef.current = false;
      setFormExpanded(true);
      // Фокус после того, как поле визуально появилось
      window.setTimeout(() => createNameInputRef.current?.focus(), 240);
    }, 190);
  };

  const handleCreateSubmit = async () => {
    if (!newMailboxName.trim()) return;

    const clientRequestId = makeClientRequestId();
    pendingCreateRequestIdsRef.current.add(clientRequestId);

    try {
      const response = await api.createMailbox({
        name: newMailboxName.trim(),
        account: newMailboxAccount.trim() || undefined,
        password: newMailboxPassword.trim() || undefined,
        notePrivate: Boolean(newMailboxNotePrivate),
        clientRequestId
      });

      if (response.success && response.data) {
        // Добавляем новый ящик в списки (заметка/сортировка подтянутся с сервера)
        const created = response.data!.mailbox;
        const echoedRequestId =
          (created as any)?.clientRequestId ? String((created as any).clientRequestId) : clientRequestId;
        // If WS already counted this creation, don't increment totals again.
        const shouldCountHere = !countedCreateRequestIdsRef.current.has(echoedRequestId);
        countedCreateRequestIdsRef.current.add(echoedRequestId);
        pendingCreateRequestIdsRef.current.delete(echoedRequestId);

        if (created?.id) optimisticCreatedMailboxIdsRef.current.add(String(created.id));
        setMailboxesByScope(prev => {
          const mineNext = sortMailboxes([created, ...prev.mine]);
          const allNext = prev.all.some(mb => mb.id === created.id)
            ? prev.all
            : sortMailboxes([created, ...prev.all]);
          return { mine: mineNext, all: allNext };
        });
        if (shouldCountHere) {
          setTotalByScope(prev => ({
            mine: (prev.mine ?? 0) + 1,
            all: (prev.all ?? 0) + 1
          }));
        }
        setNewMailboxName('');
        setNewMailboxAccount('');
        setNewMailboxPassword('');
        setNewMailboxNotePrivate(true);
        setFormExpanded(false);
        onShowNotification?.('Ящик успешно создан!', 'success');

        // UX: сразу открываем только что созданный ящик.
        if (created?.id) {
          onMailboxSelect(String(created.id), created as Mailbox);
        }
      } else {
        onShowNotification?.(response.message || 'Ошибка при создании ящика', 'error');
      }
    } catch (error: any) {
      console.error('Error creating mailbox:', error);
      onShowNotification?.(error.message || 'Ошибка при создании ящика', 'error');
    } finally {
      // If request failed before we could clear it, avoid leaking.
      pendingCreateRequestIdsRef.current.delete(clientRequestId);
    }
  };

  const handleCreateKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (!formExpanded) return;
    if (!newMailboxName.trim()) return;
    void handleCreateSubmit();
  };

  const handleCancel = () => {
    setNewMailboxName('');
    setNewMailboxAccount('');
    setNewMailboxPassword('');
    setNewMailboxNotePrivate(true);
    setFormExpanded(false);
  };

  const handleDeleteMailboxClick = (event: React.MouseEvent<HTMLButtonElement>, mailbox: Mailbox) => {
    event.preventDefault();
    event.stopPropagation();
    if (!user.isAdmin) return;
    setDeleteCandidate(mailbox);
  };

  const refreshCurrentList = useCallback(() => {
    const activeScope: 'mine' | 'all' = isChildRef.current ? 'mine' : scopeRef.current;
    void loadMailboxesRef.current({
      nextScope: activeScope,
      nextPage: 1,
      q: searchTermRef.current,
      append: false,
      silent: true,
      allowFlash: false
    });
    void syncSearchTotals(searchTermRef.current);
  }, [syncSearchTotals]);

  const loadTransferOptions = async (mailbox: Mailbox, q = '', resetSelection = false) => {
    setTransferLoading(true);
    try {
      const response = await api.getMailboxTransferOptions(String(mailbox.id), q);
      if (response.success && response.data) {
        setTransferOptions(response.data);
        if (resetSelection) {
          setSelectedTransferChildIds(new Set((response.data.assignedChildUserIds || []).map(Number)));
          setKingReceivesMetaNotifications(Boolean(response.data.kingReceivesMetaNotifications));
        }
      } else {
        onShowNotification?.(response.message || 'Не удалось загрузить передачу ящика', 'error');
        if (resetSelection) setTransferCandidate(null);
      }
    } catch (error: any) {
      onShowNotification?.(error.message || 'Не удалось загрузить передачу ящика', 'error');
      if (resetSelection) setTransferCandidate(null);
    } finally {
      setTransferLoading(false);
    }
  };

  useEffect(() => {
    if (!user.isAdmin || !transferCandidate || !transferOptions || transferOptions.mode === 'reclaim') return;

    if (transferSearchDebounceRef.current) {
      window.clearTimeout(transferSearchDebounceRef.current);
    }

    transferSearchDebounceRef.current = window.setTimeout(() => {
      const key = `${String(transferCandidate.id)}::${transferSearchTerm.trim()}`;
      if (lastTransferSearchKeyRef.current === key) return;
      lastTransferSearchKeyRef.current = key;
      void loadTransferOptions(transferCandidate, transferSearchTerm, false);
    }, 250);

    return () => {
      if (transferSearchDebounceRef.current) {
        window.clearTimeout(transferSearchDebounceRef.current);
        transferSearchDebounceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.isAdmin, transferCandidate?.id, transferOptions?.mode, transferSearchTerm]);

  const handleTransferMailboxClick = async (event: React.MouseEvent<HTMLButtonElement>, mailbox: Mailbox) => {
    event.preventDefault();
    event.stopPropagation();
    if (isChild || !(mailbox as any)?.canTransfer) return;

    setTransferCandidate(mailbox);
    setTransferOptions(null);
    setTransferLoading(true);
    setSelectedTransferChildIds(new Set());
    setKingReceivesMetaNotifications(false);
    setTransferSearchTerm('');
    lastTransferSearchKeyRef.current = `${String(mailbox.id)}::`;
    await loadTransferOptions(mailbox, '', true);
  };

  const handleToggleTransferChild = (childId: number) => {
    setSelectedTransferChildIds(prev => {
      const next = new Set(prev);
      if (next.has(childId)) next.delete(childId);
      else next.add(childId);
      return next;
    });
  };

  const closeTransferModal = () => {
    if (transferSaving) return;
    setTransferCandidate(null);
    setTransferOptions(null);
    setSelectedTransferChildIds(new Set());
    setKingReceivesMetaNotifications(false);
    setTransferSearchTerm('');
  };

  const handleConfirmTransfer = async () => {
    if (!transferCandidate || !transferOptions || transferSaving) return;
    const mailboxId = String(transferCandidate.id);
    setTransferSaving(true);
    try {
      const response = transferOptions.mode === 'reclaim'
        ? await api.reclaimMailbox(mailboxId)
        : await api.assignMailboxChildren(
          mailboxId,
          Array.from(selectedTransferChildIds),
          kingReceivesMetaNotifications
        );

      if (response.success) {
        onShowNotification?.(
          transferOptions.mode === 'reclaim'
            ? 'Ящик станет твоим'
            : (selectedTransferChildIds.size ? 'Ящик передан выбранным получателям' : 'Передача ящика отключена'),
          'success'
        );
        setTransferCandidate(null);
        setTransferOptions(null);
        setSelectedTransferChildIds(new Set());
        setKingReceivesMetaNotifications(false);
        refreshCurrentList();
      } else {
        onShowNotification?.(response.message || 'Не удалось применить передачу ящика', 'error');
      }
    } catch (error: any) {
      onShowNotification?.(error.message || 'Не удалось применить передачу ящика', 'error');
    } finally {
      setTransferSaving(false);
    }
  };

  const handleConfirmDeleteMailbox = async () => {
    if (!deleteCandidate || deletingMailboxId) return;
    const mailboxId = String(deleteCandidate.id);
    const mailboxName = mailboxAddress(deleteCandidate);
    setDeletingMailboxId(mailboxId);
    try {
      const response = await api.deleteAdminMailbox(mailboxId);
      if (response.success) {
        setMailboxesByScope(prev => ({
          mine: prev.mine.filter(mb => String(mb.id) !== mailboxId),
          all: prev.all.filter(mb => String(mb.id) !== mailboxId)
        }));
        setMailboxLiveMetaById(prev => {
          if (!(mailboxId in prev)) return prev;
          const next = { ...prev };
          delete next[mailboxId];
          return next;
        });
        setHighlightedMailboxIds(prev => {
          if (!prev.has(mailboxId)) return prev;
          const next = new Set(prev);
          next.delete(mailboxId);
          return next;
        });
        setTotalByScope(prev => ({
          mine: typeof prev.mine === 'number' ? Math.max(0, prev.mine - 1) : prev.mine,
          all: typeof prev.all === 'number' ? Math.max(0, prev.all - 1) : prev.all
        }));
        onShowNotification?.(
          `Ящик ${mailboxName} удалён. Писем удалено: ${(response.data as any)?.deletedEmails ?? 0}`,
          'success'
        );
        onMailboxDeleted?.(mailboxId);
        setDeleteCandidate(null);
      } else {
        onShowNotification?.(response.message || 'Не удалось удалить ящик', 'error');
      }
    } catch (error: any) {
      onShowNotification?.(error.message || 'Не удалось удалить ящик', 'error');
    } finally {
      setDeletingMailboxId(null);
    }
  };

  if (!hasLoadedOnce && isLoading) {
    return (
      <div className="mailbox-loading">
        <div className="loading-spinner"></div>
        <p>Загрузка ящиков...</p>
      </div>
    );
  }

  return (
    <div className="mailbox-list">
      {/* Tabs */}
      {!isChild && (
        <div className="mailbox-scope-bar">
          <div className="mailbox-scope-switcher">
            <button
              type="button"
              className={`mailbox-scope-btn ${effectiveScope === 'mine' ? 'active' : ''}`}
              onClick={() => setScope('mine')}
            >
              <i className="fas fa-user"></i>
              Мои ({typeof totalByScope.mine === 'number' ? totalByScope.mine : '…'})
            </button>

            <button
              type="button"
              className={`mailbox-scope-btn ${effectiveScope === 'all' ? 'active' : ''}`}
              onClick={() => setScope('all')}
            >
              <i className="fas fa-layer-group"></i>
              Все ({typeof totalByScope.all === 'number' ? totalByScope.all : '…'})
            </button>
            <div className={`mailbox-scope-indicator ${effectiveScope === 'mine' ? 'left' : 'right'}`}></div>
          </div>
        </div>
      )}
      {/* Search */}
      <div className="search-section">
        <div className="search-input-container">
          <input
            type="text"
            name="mailbox_search"
            placeholder="Поиск: адрес / заметка..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            inputMode="search"
          />
          <span className="search-icon"><i className="fas fa-search"></i></span>
          {searchTerm && (
            <button
              type="button"
              className="search-clear-btn"
              onClick={() => setSearchTerm('')}
              aria-label="Очистить поиск"
              title="Очистить поиск"
            >
              <i className="fas fa-times" aria-hidden="true"></i>
            </button>
          )}
        </div>
      </div>

      {/* Create Mailbox */}
      <div className="create-section">
        <div className={`create-form-container ${formExpanded ? 'expanded' : 'collapsed'}`}>
          
          {/* Дополнительные поля (появляются при расширении) */}
          <div className={`additional-create-fields ${formExpanded ? 'show' : 'hide'}`}>
            <div className="fields-container">
              <div className="create-input-group">
                <input
                  type="text"
                  name="mailbox_name"
                  autoComplete="off"
                  placeholder="Имя ящика"
                  value={newMailboxName}
                  onChange={(e) => setNewMailboxName(e.target.value)}
                  onKeyDown={handleCreateKeyDown}
                  className="create-input"
                  ref={createNameInputRef}
                  tabIndex={formExpanded ? 0 : -1}
                />
                <button 
                  className="random-btn"
                  onClick={generateRandomName}
                  onPointerDown={createRipple}
                  title="Сгенерировать случайное имя"
                  tabIndex={formExpanded ? 0 : -1}
                >
                  <i className="fas fa-dice"></i>
                </button>
              </div>
              <div className="create-input-group">
                <input
                  type="text"
                  name="mailbox_note"
                  autoComplete="off"
                  placeholder="Заметка"
                  value={newMailboxAccount}
                  onChange={(e) => setNewMailboxAccount(e.target.value)}
                  onKeyDown={handleCreateKeyDown}
                  className="create-input"
                  tabIndex={formExpanded ? 0 : -1}
                />
              </div>
              <div className="create-input-group">
                <input
                  // Use text + CSS masking to avoid browser password manager prompts.
                  type="text"
                  name="mailbox_create_password"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-bwignore="true"
                  placeholder="Пароль (не обязательно)"
                  value={newMailboxPassword}
                  onChange={(e) => setNewMailboxPassword(e.target.value)}
                  onKeyDown={handleCreateKeyDown}
                  className="create-input mailbox-password-input"
                  tabIndex={formExpanded ? 0 : -1}
                />
              </div>
              <div className="create-privacy-row">
                <label className="create-privacy-label">
                  <input
                    type="checkbox"
                    className="create-privacy-checkbox"
                    checked={newMailboxNotePrivate}
                    onChange={(e) => setNewMailboxNotePrivate(e.target.checked)}
                  />
                  <span className="create-privacy-custom" aria-hidden="true">
                    <i className="fas fa-check" aria-hidden="true"></i>
                  </span>
                  <span className="create-privacy-text">Заметка видна только мне</span>
                </label>
                <Tooltip
                  content={'При включенной функции свои заметки видит только создатель почты. Также поиск по заметкам будет работать только для владельца.'}
                  placement="top"
                >
                  <button type="button" className="create-privacy-info-btn" aria-label="Информация">
                    <i className="fas fa-info-circle" aria-hidden="true"></i>
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>

          {/* Кнопки: обе группы всегда в DOM, морфинг — прерываемыми transition */}
          <div className="create-button-container">
            <button 
              className={`create-single-btn ${formExpanded ? 'is-hidden' : ''}`}
              onClick={toggleCreateForm}
              onPointerDown={createRipple}
              tabIndex={formExpanded ? -1 : 0}
              aria-hidden={formExpanded}
            >
              <i className="fas fa-plus"></i>
              Создать ящик
            </button>
            <div
              className={`create-dual-buttons ${formExpanded ? '' : 'is-hidden'}`}
              aria-hidden={!formExpanded}
            >
              <button 
                className="create-cancel-btn"
                onClick={handleCancel}
                onPointerDown={createRipple}
                tabIndex={formExpanded ? 0 : -1}
              >
                <i className="fas fa-times"></i>
                Отмена
              </button>
              <button 
                className="create-submit-btn"
                onClick={handleCreateSubmit}
                onPointerDown={createRipple}
                disabled={!newMailboxName.trim()}
                tabIndex={formExpanded ? 0 : -1}
              >
                <i className="fas fa-check"></i>
                Создать
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* Mailbox List */}
      <div className="mailbox-items" ref={mailboxItemsRef} onScroll={handleMailboxItemsScroll}>
        {(isLoading && mailboxes.length === 0) ? (
          <div className="mailbox-loading-inline">Загрузка...</div>
        ) : mailboxes.length === 0 ? (
          <div className="empty-state">
            <p>Ящики не найдены</p>
            {searchTerm && (
              <button onClick={() => setSearchTerm('')}>
                Очистить поиск
              </button>
            )}
          </div>
        ) : (
          <div className={`mailbox-list-scope ${entranceActive ? 'entering' : ''}`} key={scope}>
                {virtualization.topSpacer > 0 && (
                  <div className="mailbox-virtual-spacer" style={{ height: `${virtualization.topSpacer}px` }} />
                )}
                {mailboxes.slice(virtualization.start, virtualization.end).map((mailbox, mailboxIdx) => {
                  const live = mailboxLiveMetaById[mailbox.id];
                  const viewEmailCount = Number(live?.emailCount ?? mailbox.emailCount ?? 0);
                  const viewLastEmailAt = live?.lastEmailAt ?? mailbox.lastEmailAt;
                  const mailboxFullAddress = mailboxAddress(mailbox);
                  const ownerRelation = (mailbox as any)?.ownerRelation || null;
                  const isChildOwnedMailbox = ownerRelation?.type === 'child';
                  const assignedChildren = ownerRelation?.type === 'assigned_children' && Array.isArray(ownerRelation?.children)
                    ? ownerRelation.children
                    : [];
                  const isAssignedChildrenMailbox = assignedChildren.length > 0;
                  const isDecoratedChildMailbox = isChildOwnedMailbox || isAssignedChildrenMailbox;
                  const mailboxDisplayName = isDecoratedChildMailbox ? mailbox.name : mailboxFullAddress;
                  const singleAssignedChild = assignedChildren.length === 1 ? assignedChildren[0] : null;
                  const childLabel = isChildOwnedMailbox
                    ? (ownerRelation?.label ? String(ownerRelation.label) : 'Получатель')
                    : (singleAssignedChild?.label ? String(singleAssignedChild.label) : 'Получатель');
                  const childAvatarUrl = isChildOwnedMailbox
                    ? (ownerRelation?.avatarUrl ? String(ownerRelation.avatarUrl) : null)
                    : (singleAssignedChild?.avatarUrl ? String(singleAssignedChild.avatarUrl) : null);
                  const childAvatarBroken = childAvatarUrl ? Boolean(brokenChildAvatarUrls[childAvatarUrl]) : false;
                  const childIcon = (
                    <span
                      className={`mailbox-child-icon ${childAvatarUrl && !childAvatarBroken ? 'has-avatar' : ''}`}
                      style={{ color: (isChildOwnedMailbox ? ownerRelation?.color : singleAssignedChild?.color) || '#1E9BFF' }}
                      aria-hidden="true"
                    >
                      {childAvatarUrl && !childAvatarBroken ? (
                        <img
                          src={childAvatarUrl}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          onError={() => setBrokenChildAvatarUrls(prev => (
                            prev[childAvatarUrl] ? prev : { ...prev, [childAvatarUrl]: true }
                          ))}
                        />
                      ) : (
                        <i className="fas fa-user"></i>
                      )}
                    </span>
                  );
                  const assignedChildrenTooltip = assignedChildren
                    .map((child: any) => child?.label || child?.username || child?.telegramId || `ID ${child?.id}`)
                    .filter(Boolean)
                    .join(', ');
                  return (
                    <div
                      key={mailbox.id}
                      style={{ ['--enter-i' as any]: mailboxIdx }}
                      className={`mailbox-item ${selectedMailbox === mailbox.id ? 'selected' : ''} ${highlightedMailboxIds.has(String(mailbox.id)) ? 'has-new-mail' : ''}`}
                      onMouseEnter={() => setHoveredMailboxId(mailbox.id)}
                      onMouseLeave={() => setHoveredMailboxId(prev => (prev === mailbox.id ? null : prev))}
                      onPointerDown={(e) => {
                        // Ripple только по самой карточке, не по кнопкам внутри
                        if (!(e.target as HTMLElement).closest('button')) createRipple(e);
                      }}
                      onClick={() => {
                        onMailboxSelect(mailbox.id, mailbox);
                      }}
                    >
                    <div className="mailbox-info">
                      <div className="mailbox-name">
                        {(isChildOwnedMailbox || assignedChildren.length === 1) && (
                          <>
                            {childIcon}
                            <Tooltip
                              content={childLabel}
                              className="mailbox-child-badge"
                              onlyWhenTruncated
                            >
                              <span
                                className="mailbox-child-badge-inner"
                                style={{ '--child-color': (isChildOwnedMailbox ? ownerRelation?.color : singleAssignedChild?.color) || '#1E9BFF' } as React.CSSProperties}
                              >
                                <span className="mailbox-child-badge-text">{childLabel}</span>
                              </span>
                            </Tooltip>
                          </>
                        )}
                        {assignedChildren.length > 1 && (
                          <Tooltip content={assignedChildrenTooltip || 'Пользователи'} placement="top">
                            <span className="mailbox-child-avatar-stack" aria-label={assignedChildrenTooltip || 'Пользователи'}>
                              {assignedChildren.slice(0, 4).map((child: any, idx: number) => {
                                const avatarUrl = child?.avatarUrl ? String(child.avatarUrl) : null;
                                const avatarBroken = avatarUrl ? Boolean(brokenChildAvatarUrls[avatarUrl]) : false;
                                return (
                                  <span
                                    key={`${child?.id || idx}`}
                                    className={`mailbox-child-stack-avatar ${avatarUrl && !avatarBroken ? 'has-avatar' : ''}`}
                                    style={{ color: child?.color || '#1E9BFF', zIndex: 8 - idx } as React.CSSProperties}
                                  >
                                    {avatarUrl && !avatarBroken ? (
                                      <img
                                        src={avatarUrl}
                                        alt=""
                                        loading="lazy"
                                        decoding="async"
                                        onError={() => setBrokenChildAvatarUrls(prev => (
                                          prev[avatarUrl] ? prev : { ...prev, [avatarUrl]: true }
                                        ))}
                                      />
                                    ) : (
                                      <i className="fas fa-user"></i>
                                    )}
                                  </span>
                                );
                              })}
                              {assignedChildren.length > 4 && (
                                <span className="mailbox-child-stack-more">+{assignedChildren.length - 4}</span>
                              )}
                            </span>
                          </Tooltip>
                        )}
                        {hoveredMailboxId === mailbox.id && !isDecoratedChildMailbox ? (
                          <Tooltip
                            content={mailboxFullAddress}
                            className="mailbox-address"
                            onlyWhenTruncated
                          >
                            {mailboxDisplayName}
                          </Tooltip>
                        ) : (
                          <span className="mailbox-address">{mailboxDisplayName}</span>
                        )}
                        {Boolean((mailbox as any)?.isShared) && (
                          hoveredMailboxId === mailbox.id ? (
                            <Tooltip
                              content="Ящик расшарен в ваш профиль"
                              placement="top"
                            >
                              <span
                                className="mailbox-shared-badge"
                                aria-label="Расшаренный ящик"
                              >
                                <i className="fas fa-share-alt"></i>
                                shared
                              </span>
                            </Tooltip>
                          ) : (
                            <span
                              className="mailbox-shared-badge"
                              aria-label="Расшаренный ящик"
                            >
                              <i className="fas fa-share-alt"></i>
                              shared
                            </span>
                          )
                        )}
                        {mailbox.hasPassword && <span className="lock-icon"><i className="fas fa-lock"></i></span>}
                      </div>
                      <div className="mailbox-stats">
                        {mailbox.account && (
                          hoveredMailboxId === mailbox.id ? (
                            <Tooltip content={mailbox.account} className="mailbox-note" onlyWhenTruncated>
                              {mailbox.account}
                            </Tooltip>
                          ) : (
                            <span className="mailbox-note">{mailbox.account}</span>
                          )
                        )}
                        <span className="email-count">
                          {viewEmailCount} {pluralizeRu(viewEmailCount, 'письмо', 'письма', 'писем')}
                        </span>
                        <span className="last-checked">
                          {viewLastEmailAt
                            ? formatDateMsk(viewLastEmailAt)
                            : (mailbox.createdAt ? formatDateMsk(mailbox.createdAt) : '—')}
                        </span>
                      </div>
                    </div>
                    {!isChild && Boolean((mailbox as any)?.canTransfer) && (
                      <button
                        type="button"
                        className="mailbox-transfer-btn"
                        onClick={(event) => handleTransferMailboxClick(event, mailbox)}
                        title={ownerRelation?.type === 'child' ? 'Сделать ящик твоим' : 'Передать ящик'}
                        aria-label={`${ownerRelation?.type === 'child' ? 'Сделать твоим' : 'Передать'} ящик ${mailboxAddress(mailbox)}`}
                        disabled={transferSaving && String(transferCandidate?.id || '') === String(mailbox.id)}
                      >
                        <i className="fas fa-right-left" aria-hidden="true"></i>
                      </button>
                    )}
                    {user.isAdmin && (
                      <button
                        type="button"
                        className="mailbox-delete-btn"
                        onClick={(event) => handleDeleteMailboxClick(event, mailbox)}
                        title="Удалить ящик полностью"
                        aria-label={`Удалить ящик ${mailboxAddress(mailbox)}`}
                        disabled={deletingMailboxId === String(mailbox.id)}
                      >
                        <i className="fas fa-trash" aria-hidden="true"></i>
                      </button>
                    )}
                    <div className="mailbox-arrow" aria-hidden="true">
                      <i className="fas fa-chevron-right"></i>
                    </div>
                    </div>
                  );
                })}
                {virtualization.bottomSpacer > 0 && (
                  <div className="mailbox-virtual-spacer" style={{ height: `${virtualization.bottomSpacer}px` }} />
                )}
          </div>
        )}

        {mailboxes.length > 0 && hasMore && (
          <div className="mailbox-load-more">
            <button
              type="button"
              className="load-more-btn"
              onClick={handleLoadMore}
              disabled={isLoadingMore}
            >
              {isLoadingMore ? 'Загрузка...' : 'Загрузить ещё'}
            </button>
          </div>
        )}
      </div>
      {deleteCandidate && (
        <div
          className="mailbox-delete-modal-overlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !deletingMailboxId) {
              setDeleteCandidate(null);
            }
          }}
        >
          <div className="mailbox-delete-modal" role="dialog" aria-modal="true" aria-labelledby="mailbox-delete-title">
            <div className="mailbox-delete-modal-icon" aria-hidden="true">
              <i className="fas fa-trash"></i>
            </div>
            <h3 id="mailbox-delete-title">Удалить ящик?</h3>
            <p>
              Вы точно хотите полностью удалить{' '}
              <b>{mailboxAddress(deleteCandidate)}</b>?
            </p>
            <div className="mailbox-delete-warning">
              Будут удалены сам ящик, все письма и сохранённое содержимое писем. Это действие нельзя отменить.
            </div>
            <div className="mailbox-delete-modal-actions">
              <button
                type="button"
                className="mailbox-delete-cancel-btn"
                onClick={() => setDeleteCandidate(null)}
                disabled={Boolean(deletingMailboxId)}
              >
                Отмена
              </button>
              <button
                type="button"
                className="mailbox-delete-confirm-btn"
                onClick={handleConfirmDeleteMailbox}
                onPointerDown={createRipple}
                disabled={Boolean(deletingMailboxId)}
              >
                {deletingMailboxId ? 'Удаляем...' : 'Удалить'}
              </button>
            </div>
          </div>
        </div>
      )}
      {transferCandidate && (
        <div
          className="mailbox-delete-modal-overlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !transferSaving) {
              closeTransferModal();
            }
          }}
        >
          <div className="mailbox-delete-modal mailbox-transfer-modal" role="dialog" aria-modal="true" aria-labelledby="mailbox-transfer-title">
            <div className="mailbox-transfer-modal-icon" aria-hidden="true">
              <i className="fas fa-right-left"></i>
            </div>
            <h3 id="mailbox-transfer-title">
              {transferOptions?.mode === 'reclaim' ? 'Сделать ящик твоим?' : 'Передать ящик'}
            </h3>
            <p>
              Ящик <b>{mailboxAddress(transferCandidate)}</b>
            </p>

            {transferLoading && !transferOptions ? (
              <div className="mailbox-transfer-loading">Загрузка пользователей...</div>
            ) : transferOptions?.mode === 'reclaim' ? (
              <div className="mailbox-delete-warning mailbox-transfer-note">
                Ящик станет твоим. Дополнительные бейджики пропадут, а Meta/Facebook уведомления по этому ящику будут приходить только тебе.
              </div>
            ) : (
              <>
                {user.isAdmin && (
                  <div className="mailbox-transfer-search">
                    <input
                      type="text"
                      value={transferSearchTerm}
                      onChange={(event) => setTransferSearchTerm(event.target.value)}
                      placeholder="Поиск: имя, @username или Telegram ID"
                      disabled={transferSaving}
                    />
                  </div>
                )}
                <div className="mailbox-transfer-children">
                  {(transferOptions?.children || []).map((child: any) => {
                    const checked = selectedTransferChildIds.has(Number(child.id));
                    const avatarUrl = child?.avatarUrl ? String(child.avatarUrl) : null;
                    const avatarBroken = avatarUrl ? Boolean(brokenChildAvatarUrls[avatarUrl]) : false;
                    const label = child?.label || child?.username || child?.telegramId || `ID ${child.id}`;
                    return (
                      <label key={child.id} className={`mailbox-transfer-child ${checked ? 'is-selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => handleToggleTransferChild(Number(child.id))}
                          disabled={transferSaving}
                        />
                        <span
                          className={`mailbox-transfer-child-avatar ${avatarUrl && !avatarBroken ? 'has-avatar' : ''}`}
                          style={{ color: child?.color || '#1E9BFF' }}
                        >
                          {avatarUrl && !avatarBroken ? (
                            <img
                              src={avatarUrl}
                              alt=""
                              loading="lazy"
                              decoding="async"
                              onError={() => setBrokenChildAvatarUrls(prev => (
                                prev[avatarUrl] ? prev : { ...prev, [avatarUrl]: true }
                              ))}
                            />
                          ) : (
                            <i className="fas fa-user"></i>
                          )}
                        </span>
                        <span className="mailbox-transfer-child-name">{label}</span>
                        <span className="mailbox-transfer-check" aria-hidden="true">
                          <i className="fas fa-check"></i>
                        </span>
                      </label>
                    );
                  })}
                  {!transferLoading && (transferOptions?.children || []).length === 0 && (
                    <div className="mailbox-transfer-empty">Никого не найдено</div>
                  )}
                </div>

                <label className="mailbox-transfer-keep-king">
                  <input
                    type="checkbox"
                    checked={kingReceivesMetaNotifications}
                    onChange={(event) => setKingReceivesMetaNotifications(event.target.checked)}
                    disabled={transferSaving}
                  />
                  <span>Продолжать получать Facebook/Meta уведомления с этого ящика</span>
                </label>
              </>
            )}

            <div className="mailbox-delete-modal-actions">
              <button
                type="button"
                className="mailbox-delete-cancel-btn"
                onClick={closeTransferModal}
                disabled={transferSaving}
              >
                Отмена
              </button>
              <button
                type="button"
                className="mailbox-transfer-confirm-btn"
                onClick={handleConfirmTransfer}
                onPointerDown={createRipple}
                disabled={transferSaving || transferLoading || !transferOptions}
              >
                {transferSaving
                  ? 'Сохраняем...'
                  : (transferOptions?.mode === 'reclaim' ? 'Сделать моим' : 'Сохранить')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MailboxList;
